import { Slot } from 'radix-ui'
import { type VariantProps, cva } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'

/**
 * Every variant carries the full state set the design system requires: default,
 * hover, focus-visible, active, disabled, and loading. `loading` is a real prop
 * rather than a caller-managed spinner so a pending action can never look idle.
 *
 * Focus is the outline recipe for every variant (the `outline` variant included),
 * so the ring slot stays free for callers such as segmented selections. Solid
 * variants put their sheen in the shadow slot (`shadow-button`), never the ring.
 */
const buttonVariants = cva(
    cn(
        'inline-flex shrink-0 select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium',
        'transition-[color,background-color,border-color,box-shadow] duration-(--animate-duration) ease-(--ease-out-quart)',
        'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        'disabled:pointer-events-none disabled:opacity-50',
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
    ),
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground shadow-button hover:bg-primary/90 active:brightness-95',
                destructive:
                    'bg-destructive text-destructive-foreground shadow-button hover:bg-destructive/90 active:brightness-95',
                // The system's secondary button: a card-coloured chip with a hairline and a resting shadow.
                outline:
                    'border border-input bg-card text-foreground shadow-xs hover:bg-surface-raised active:bg-surface-hover',
                secondary: 'bg-secondary text-secondary-foreground hover:bg-surface-hover active:bg-surface-hover/80',
                ghost: 'hover:bg-accent hover:text-accent-foreground active:bg-surface-hover',
                link: 'text-primary-text underline-offset-4 hover:underline'
            },
            size: {
                // Comfortable-dense: default sits at 2rem so toolbars stay compact.
                default: 'h-8 px-3 has-[>svg]:px-2.5',
                sm: "h-7 gap-1 rounded-md px-2.5 text-xs has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3.5",
                lg: 'h-9 px-4 has-[>svg]:px-3',
                // 1.5rem for segmented options and inline chip actions. The 5px radius keeps the
                // chip concentric inside a 6px track with 2px padding.
                xs: "h-6 gap-1 rounded-[5px] px-2 text-xs [&_svg:not([class*='size-'])]:size-3.5",
                icon: 'size-8',
                'icon-sm': "size-7 [&_svg:not([class*='size-'])]:size-3.5",
                'icon-xs': "size-6 rounded-[5px] [&_svg:not([class*='size-'])]:size-3.5"
            }
        },
        defaultVariants: { variant: 'default', size: 'default' }
    }
)

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
    asChild?: boolean
    loading?: boolean
}

function Button({ className, variant, size, asChild = false, loading = false, children, ...props }: ButtonProps) {
    if (asChild) {
        return (
            <Slot.Root data-slot="button" className={cn(buttonVariants({ variant, size }), className)} {...props}>
                {children}
            </Slot.Root>
        )
    }

    return (
        <button
            data-slot="button"
            className={cn(buttonVariants({ variant, size }), className)}
            {...props}
            // After the spread, not before it: a caller passing `disabled={false}`
            // alongside `loading` would otherwise re-enable the button mid-request
            // and let the action fire twice.
            disabled={props.disabled || loading}
            aria-busy={loading || undefined}>
            {loading && <Loader2 className="animate-spin" aria-hidden />}
            {children}
        </button>
    )
}

export { Button, buttonVariants }
